import React, { useState, useEffect } from "react";
import {
  useCloseRaffleMutation,
  useGetRaffleWinnerQuery,
} from "../../store/api";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Switch,
  Typography,
  Divider,
} from "@mui/material";
import { Email, EmojiEvents, Person, Phone } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { Trophy } from "lucide-react";

type WinnerSearchProps = {
  raffleId: string;
  setOpen: (value: boolean) => void;
  ticket: number;
};

const WinnerSearchResults: React.FC<WinnerSearchProps> = ({
  raffleId,
  setOpen,
  ticket,
}) => {
  const [sendEmail, setSendEmail] = useState(false);
  const { data, error, isLoading, isFetching } = useGetRaffleWinnerQuery(
    { raffleId, ticket },
    { refetchOnFocus: false }
  );
  const [closeRaffleHandler] = useCloseRaffleMutation();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSendEmail(event.target.checked);
  };

  const handleCloseRaffle = async () => {
    try {
      const result = await closeRaffleHandler({
        conf: {
          sendEmail,
          winnerEmail: data?.email,
        },
        raffleId,
      }).unwrap();

      if (result) {
        enqueueSnackbar("Rifa cerrada con éxito!", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
        setOpen(false);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        enqueueSnackbar("Error al cerrar la rifa", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      }
    } catch {
      enqueueSnackbar("Error al cerrar la rifa", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    }
  };

  if (isLoading || isFetching) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="textSecondary">
          No se encontró ningún ganador con ese número.
        </Typography>
        <Box mt={4}>
          <Button onClick={handleCloseRaffle} variant="contained" fullWidth>
            Cerrar rifa
          </Button>
        </Box>
      </Box>
    );
  }

  const handleViewPurchases = () => {
    window.open(`/self-verification/${data.email}?raffleId=${raffleId}`, '_blank');
  };

  return (
    <Box mt={3}>
      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Trophy color="yellow" />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ ml: 1, fontFamily: "inherit" }}
            >
              ¡Ganador encontrado!
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Paper elevation={1} sx={{ borderRadius: 2, p: 3 }}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Person fontSize="small" color="action" />
                <Typography>{data.name}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Email fontSize="small" color="action" />
                <Typography>{data.email}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Phone fontSize="small" color="action" />
                <Typography>{data.phone}</Typography>
              </Box>
              <Box display="flex" alignItems="center" mt={2}>
                <Button variant="contained" color="primary" onClick={handleViewPurchases}>Ver ordenes de compra</Button>
              </Box>
            </Box>
          </Paper>

          <Box mt={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleCloseRaffle}
            >
              Cerrar rifa
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WinnerSearchResults;
